<script lang="ts">
  import axios from 'axios';
  import { untrack } from 'svelte';
  import ConteneurLarge from '../ui/ConteneurLarge.svelte';
  import Lien from '../ui/Lien.svelte';
  import { détecteRendu } from '../utils/rendu.svelte';
  import Avertissements from './Avertissements.svelte';
  import { fabriqueDExigence, type Exigence, type ReferentielSelectionne } from './exigence.type';
  import Panneau from './panneau/Panneau.svelte';
  import { exigencesStore } from './stores/exigences.store';
  import { exigencesFiltrees } from './stores/exigencesFiltrees.store';
  import type { Comparaison } from './tableaux/configuration.type';
  import TableauCorrespondancesExigences from './tableaux/TableauCorrespondancesExigences.svelte';
  import TableauExigencesSimple from './tableaux/TableauExigencesSimple.svelte';

  type Props = {
    featureFlagNis2CyFun23: boolean;
    exigences?: Exigence[];
  };
  const { featureFlagNis2CyFun23, exigences: exigencesExternes }: Props = $props();
  const exigencesInitiales = untrack(() => exigencesExternes);
  if (exigencesInitiales) {
    exigencesStore.initialise(exigencesInitiales);
  }

  const rendu = détecteRendu();

  let exigences = $state<Exigence[]>([]);
  let sensComparaison = $state<'NIS2_VERS_CIBLE' | 'SOURCE_VERS_NIS2'>('NIS2_VERS_CIBLE');

  let mode = $state<'LISTE' | Comparaison>('LISTE');

  let referentielSelectionne = $state<ReferentielSelectionne | ''>('');
  let langueSelectionnee = $state<'FR' | 'EN'>('FR');
  let chargement = $state(false);

  const recupereLesExigences = async () => {
    const axiosResponse = await axios.get<Record<string, unknown>[]>('/api/exigences-nis2', {
      params: { source, cible, langue: langueSelectionnee },
    });
    exigences = axiosResponse.data.map((e) => fabriqueDExigence(source.length === 0 ? 'NIS2' : source, cible, e));
    $exigencesStore = exigences;
  };

  const source = $derived(
    sensComparaison === 'NIS2_VERS_CIBLE'
      ? 'NIS2'
      : ((referentielSelectionne.length > 0 ? referentielSelectionne : 'NIS2') as Exclude<ReferentielSelectionne, ''>)
  );

  const cible = $derived(sensComparaison === 'SOURCE_VERS_NIS2' ? 'NIS2' : referentielSelectionne);

  const lienExportTableau = $derived(
    `/api/exigences-nis2.csv?${new URLSearchParams({
      ...(source && { source }),
      ...(cible && { cible }),
      langue: langueSelectionnee,
    }).toString()}`
  );

  $effect(() => {
    const charge = async () => {
      chargement = true;
      await recupereLesExigences();

      chargement = false;
      if (!referentielSelectionne) {
        mode = 'LISTE';
      } else {
        mode =
          sensComparaison === 'NIS2_VERS_CIBLE'
            ? `COMPARAISON_NIS2_${referentielSelectionne}`
            : `COMPARAISON_${referentielSelectionne}_NIS2`;
      }
    };
    charge();
  });

  const traceTelechargement = (url: string) => {
    window._paq?.push(['trackLink', `https://${window.location.host}${url}`, 'download']);
  };

  const libelleFichierTelechargement = $derived(
    langueSelectionnee === 'FR' ? 'Télécharger ReCyf' : 'Télécharger ReCyf (en)'
  );
  const lienFichierTelechargement = $derived(
    langueSelectionnee === 'FR'
      ? '/documents-ressources/20260317_NIS_V2_ReCyF_v2.5.pdf'
      : '/documents-ressources/20260401_NIS_V2_ReCyF_v2.5_EN.pdf'
  );
</script>

<ConteneurLarge mode={mode === 'LISTE' ? 'STANDARD' : 'LARGE'}>
  <Avertissements estBureau={rendu.estBureau} />
  <div class="entete">
    <div class="titre">
      <h2>Le Référentiel Cyber France (ReCyF)</h2>
      <div class="telechargements">
        <Lien
          href={lienFichierTelechargement}
          libelle={libelleFichierTelechargement}
          telechargement=""
          telechargementDetails="PDF - 965,8 ko"
          surClic={() => traceTelechargement(lienFichierTelechargement)}
        />
        <Lien
          href="/documents-ressources/20260317_NIS_V2_Suivi des modifications ReCyF v2.4 vers v2.5-vfinale.pdf"
          libelle="Télécharger le suivi des modifications"
          telechargement=""
          telechargementDetails="PDF - 1 383,4 ko"
          surClic={() =>
            traceTelechargement(
              '/documents-ressources/20260317_NIS_V2_Suivi des modifications ReCyF v2.4 vers v2.5-vfinale.pdf'
            )}
        />
        <Lien href="/guides/fiches-pratiques-recyf" libelle="Consulter les fiches pratiques ReCyF" blank />
      </div>
      <dsfr-button
        label="Exporter le tableau"
        markup="a"
        href={lienExportTableau}
        has-icon
        icon-place="left"
        icon="download-line"
        centered
      ></dsfr-button>
    </div>

    <Panneau
      source={sensComparaison === 'NIS2_VERS_CIBLE'
        ? 'NIS2'
        : ((referentielSelectionne ?? 'NIS2') as Exclude<ReferentielSelectionne, ''>)}
      bind:referentielSelectionne
      bind:langueSelectionnee
      bind:sensComparaison
      estBureau={rendu.estBureau}
      {featureFlagNis2CyFun23}
    />
  </div>
  {#if mode === 'LISTE'}
    <TableauExigencesSimple exigences={$exigencesFiltrees.exigences} {chargement} />
  {:else}
    <TableauCorrespondancesExigences {chargement} exigences={$exigencesFiltrees.exigences} comparaison={mode} />
  {/if}
  <Lien libelle="Haut de page" href="#" taille="md" icone="arrow-up-fill" />
</ConteneurLarge>

<style lang="scss">
  @use '../../../assets/styles/responsive' as *;

  h2 {
    margin-bottom: 0;
  }

  .entete {
    margin: 0 0 24px;
    padding: 0 0 16px;

    .titre {
      display: grid;
      grid-template-areas: 'titre' 'telechargement' 'export';
      margin-bottom: 1rem;

      @include a-partir-de(md) {
        grid-template-areas: 'titre export' 'telechargement telechargement';
        grid-template-columns: auto auto;
        row-gap: 8px;
        margin-bottom: 0;
      }
      h2 {
        grid-area: titre;
      }

      .telechargements {
        grid-area: telechargement;
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        margin-bottom: 1.5rem;
      }

      dsfr-button {
        grid-area: export;
        @include a-partir-de(md) {
          width: fit-content;
          justify-self: end;
        }
      }
    }
  }
</style>
